import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import {
  faArrowsRotate,
  faCopy,
  faEllipsisH,
  faSave,
  faShareFromSquare,
  faSpinner,
  faTrash,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { COPY_AUTO_TEMPLATE } from "@graphql/mutations/AutoTemplates";
import { GET_AUTO_TEMPLATES } from "@graphql/queries/AutoTemplates";
import { Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { animated, config, useSpring } from "react-spring";

import { trackOperation } from "../../../../../utils/operationTracking";
import { CopyTemplateModal } from "../../../SharedTemplatePage/CopyTemplateModal";
import { isPreviewDisabled } from "../../utils";

function ButtonGroupItem({
  label,
  icon,
  onClick,
  tooltipText,
  customClassName = null,
  dataTestId = null,
  disabled = false,
}) {
  return (
    <Tooltip title={tooltipText} arrow>
      <span>
        <button
          type="button"
          className={`${
            customClassName
              ? customClassName
              : "bg-primary text-brand hover:bg-secondary"
          } font-semibold justify-center px-4 h-[40px] text-xs text-nowrap flex flex-row items-center transition-all focus:outline-hidden disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={onClick}
          data-testid={dataTestId}
          disabled={disabled}
        >
          <FontAwesomeIcon
            icon={icon}
            className={`${label && "lg:mr-2"}`}
          />
          {label && <span className="hidden lg:block">{label}</span>}
        </button>
      </span>
    </Tooltip>
  );
}

function MenuItem({
  onClick,
  title,
  icon = null,
  dataTestId = null,
  customClassName = null,
}) {
  return (
    <div
      className={`block px-4 py-2 text-sm cursor-pointer ${
        customClassName
          ? customClassName
          : "text-primary hover:bg-secondary"
      }`}
      role="menuitem"
      onClick={onClick}
      data-testid={dataTestId}
    >
      {icon && <FontAwesomeIcon icon={icon} className="mr-2" />}{" "}
      {title}
    </div>
  );
}

export default function TemplateActionsToolbar({
  control,
  setShouldShowDeleteModal,
  loadingExtraction,
  handlePreview,
  loadingPreview,
  previewContent,
  onClearPreview,
  doesCurrentUserOwnThisTemplate,
  templateUuid,
  templateName,
  formRef,
  onShare,
  isShareDisabled,
  shareDisabledTooltip,
}) {
  const navigate = useNavigate();
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [customTemplateName, setCustomTemplateName] =
    useState(templateName);
  const [shouldShowMenu, setShouldShowMenu] = useState(false);
  const wrapperRef = useRef(null);

  const [copyAutoTemplate] = useMutation(COPY_AUTO_TEMPLATE, {
    refetchQueries: [GET_AUTO_TEMPLATES],
  });

  const {
    formState: { dirtyFields },
  } = useFormContext();

  const formData = useWatch({ control });
  const isPreviewCurrentlyDisabled = isPreviewDisabled(
    formData,
    dirtyFields,
  );

  const menuAnim = useSpring({
    config: config.wobbly,
    opacity: shouldShowMenu ? 1 : 0,
    transform: shouldShowMenu ? "scale(1)" : "scale(0.8)",
  });

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShouldShowMenu(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  useOutsideAlerter(wrapperRef);

  const handleDuplicateTemplate = () =>
    trackOperation("template-duplicate", async (ctx) => {
      setShowCopyModal(false);

      ctx.addProperties({
        templateUuid,
        originalTemplateName: templateName,
        newTemplateName: customTemplateName,
      });

      const { data } = await copyAutoTemplate({
        variables: {
          templateUuid,
          name: customTemplateName,
        },
      });

      if (!data?.copyAutoTemplate?.ok) {
        throw new Error("Failed to duplicate template");
      }

      alert("success", "Template duplicated successfully!");
      navigate("/templates");
    });

  const handleClearPreview = () => {
    onClearPreview();
  };

  const previewIcon = loadingPreview
    ? faSpinner
    : previewContent
      ? faXmark
      : faArrowsRotate;

  const previewLabel = loadingPreview
    ? "Generating..."
    : previewContent
      ? "Clear Preview"
      : "Preview";

  return (
    <>
      <CopyTemplateModal
        shouldShow={showCopyModal}
        hideModal={() => setShowCopyModal(false)}
        confirmAction={handleDuplicateTemplate}
        templateName={templateName}
        customName={customTemplateName}
        onNameChange={setCustomTemplateName}
      />

      <div className="relative flex flex-row items-center border border-primary rounded-lg shadow-xs divide-x divide-primary">
        {doesCurrentUserOwnThisTemplate && (
          <>
            <ButtonGroupItem
              label="Save"
              icon={faSave}
              onClick={() => formRef.current?.requestSubmit()}
              tooltipText="Save template"
              disabled={loadingExtraction}
              dataTestId="save-template-button"
              customClassName="bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800 rounded-l-lg"
            />
            <ButtonGroupItem
              label={previewLabel}
              icon={previewIcon}
              onClick={
                previewContent ? handleClearPreview : handlePreview
              }
              tooltipText={
                previewContent
                  ? "Clear the current preview"
                  : isPreviewCurrentlyDisabled
                    ? "There are no changes to preview."
                    : "Preview the template with sample data"
              }
              disabled={
                !previewContent &&
                (loadingPreview || isPreviewCurrentlyDisabled)
              }
              dataTestId="preview-template-button"
              customClassName={
                previewContent
                  ? "bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800"
                  : "bg-primary text-brand hover:bg-secondary"
              }
            />
            <ButtonGroupItem
              label="Share"
              icon={faShareFromSquare}
              onClick={onShare}
              tooltipText={
                shareDisabledTooltip || "Share this template"
              }
              disabled={isShareDisabled}
              dataTestId="share-template-button-header"
              customClassName="bg-primary text-brand hover:bg-secondary"
            />
          </>
        )}

        <div ref={wrapperRef} className="relative">
          <ButtonGroupItem
            label="More"
            icon={faEllipsisH}
            onClick={() => setShouldShowMenu(!shouldShowMenu)}
            dataTestId="more-button"
            customClassName={`${
              !doesCurrentUserOwnThisTemplate ? "rounded-l-lg" : ""
            } rounded-r-lg bg-primary text-brand hover:bg-secondary`}
          />
          <animated.div
            style={menuAnim}
            className={`absolute top-11 right-0 bg-primary border border-primary rounded-lg shadow-lg z-50 divide-y divide-primary min-w-[200px] overflow-hidden ${
              shouldShowMenu ? "block" : "hidden"
            }`}
          >
            <MenuItem
              title="Duplicate Template"
              icon={faCopy}
              onClick={() => {
                setShowCopyModal(true);
                setShouldShowMenu(false);
              }}
              dataTestId="duplicate-template-button"
            />
            <MenuItem
              title="Delete Template"
              icon={faTrash}
              onClick={() => {
                setShouldShowDeleteModal(true);
                setShouldShowMenu(false);
              }}
              dataTestId="delete-template-button"
              customClassName="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/50"
            />
          </animated.div>
        </div>
      </div>
    </>
  );
}
